import { createReducer, on } from "@ngrx/store";
import { addCard, insertAnexos, insertComentario, removeAnexos, removeCard, removeComentario, setGruposTrabalho, setCards,
    updateAnexos, updateCard, updateCardContrato, updateEntry, updateFlow, updateFlowProperty, updateGrupoTrabalho, removerGrupoTrabalho,
    adicionarGrupoTrabalho, addEntry, removeEntry, updateMultipleCards, updateFieldConfig, updateAjusteConfig, updateExigirComprovante, updateExpenseResponsibilities,
    setBoardColumns, appendColumnPage, setColumnLoading, upsertDeepLinkedCard } from "../actions/gerenciamento-imoveis.action";
import { GerenciamentoImoveisState, KanbanColumnMeta } from ".";
import { FluxoGerenciamentoImoveisEnum } from "src/app/features/gerenciar-imoveis/components/gerenciar-imoveis.enums";
import { CalculadoraGerenciamentoFinanceiroEntry } from "@shared/models/calculadora/calculadora.model";

/**
 * Ajusta o `total` do metadado de paginação de uma coluna (modelo híbrido).
 * No-op quando o board não foi carregado de forma paginada (columns ausente)
 * ou a coluna não existe no mapa — ex.: fluxos que ainda usam getCards.
 */
function adjustColumnTotal(
    columns: { [status: string]: KanbanColumnMeta } | undefined,
    status: string | undefined,
    delta: number,
): { [status: string]: KanbanColumnMeta } | undefined {
    if (!columns || !status || !columns[status]) return columns;
    return {
        ...columns,
        [status]: { ...columns[status], total: Math.max(0, columns[status].total + delta) },
    };
}

export const initialState: GerenciamentoImoveisState = { 
    cardsGerenciamentoImoveis: {
        [FluxoGerenciamentoImoveisEnum.FLUXO_SMART]: {
            allCards: [],
            attemptedSearchCards: false,
            attemptedSearchArquivados: false,
            attemptedSearchEstudoCaso: false,
        },
        [FluxoGerenciamentoImoveisEnum.NUCLEO_OPERACIONAL]: {
            allCards: [],
            attemptedSearchCards: false,
            attemptedSearchArquivados: false,
            attemptedSearchEstudoCaso: false,
        },
        [FluxoGerenciamentoImoveisEnum.INTERMEDIACAO_PROPOSTA]: {
            allCards: [],
            attemptedSearchCards: false,
            attemptedSearchArquivados: false,
            attemptedSearchEstudoCaso: false,
        }
    },
    currentFlowId: null,
    gruposTrabalhoState: {
        allGruposTrabalho: [],
        hasInitialLoad: false,
    },
    };

export const gerenciamentoImoveisReducer = createReducer(
    initialState,
    on(updateFlow, (previousState, { flowId }) => {
        return {
            ...previousState,
            currentFlowId: flowId,
        }
    }),
    on(updateFlowProperty, (previousState, { flowId, properties }) => {
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    ...properties
                }
            }
        }
    }),
    on(addCard, (previousState, newCard) => {
        const flow = previousState.cardsGerenciamentoImoveis[newCard.flowId];
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [newCard.flowId]: {
                    ...flow,
                    allCards: [
                        ...flow.allCards,
                        newCard,
                    ],
                    columns: adjustColumnTotal(flow?.columns, (newCard as any).status, +1),
                }
            }
        }
    }),
    on(setBoardColumns, (previousState, { flowId, cards, columns }) => {
        const columnsMeta: { [status: string]: KanbanColumnMeta } = {};
        Object.entries(columns).forEach(([status, { total, loaded }]) => {
            columnsMeta[status] = { total, loading: false, loadedOffset: loaded ?? 0 };
        });
        // Preserva cards trazidos por deep-link (injetados via upsertDeepLinkedCard)
        // que não vieram nesta página inicial — robusto à ordem de chegada entre
        // `/board` e `/card/:id`. Não entram no cursor `loadedOffset`.
        const prevFlow = previousState.cardsGerenciamentoImoveis[flowId];
        const deepLinkedCardIds = prevFlow?.deepLinkedCardIds ?? [];
        const incomingIds = new Set(cards.map((card) => card._id));
        const preservedDeepLinked = (prevFlow?.allCards ?? []).filter(
            (card) => deepLinkedCardIds.includes(card._id) && !incomingIds.has(card._id),
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...prevFlow,
                    allCards: [...cards, ...preservedDeepLinked],
                    columns: columnsMeta,
                    attemptedSearchBoard: true,
                    deepLinkedCardIds,
                },
            }
        };
    }),
    on(appendColumnPage, (previousState, { flowId, status, cards, total }) => {
        const flow = previousState.cardsGerenciamentoImoveis[flowId];
        if (!flow) return previousState;
        const existingIds = new Set(flow.allCards.map(card => card._id));
        const newOnes = cards.filter(card => !existingIds.has(card._id));
        // Cursor avança pelo nº BRUTO de cards servidos (antes do dedup): é o que
        // o servidor pulou+retornou neste offset, então o próximo offset = soma.
        // Um card já presente (ex.: deep-link) é deduplicado em `allCards` mas
        // ainda conta para o cursor, evitando pular o card seguinte.
        const prevOffset = flow.columns?.[status]?.loadedOffset ?? 0;
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...flow,
                    allCards: [...flow.allCards, ...newOnes],
                    columns: {
                        ...flow.columns,
                        [status]: { total, loading: false, loadedOffset: prevOffset + cards.length },
                    },
                },
            }
        };
    }),
    on(setColumnLoading, (previousState, { flowId, status, loading }) => {
        const flow = previousState.cardsGerenciamentoImoveis[flowId];
        if (!flow?.columns) return previousState;
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...flow,
                    columns: {
                        ...flow.columns,
                        [status]: { ...(flow.columns[status] ?? { total: 0 }), loading },
                    },
                },
            }
        };
    }),
    on(upsertDeepLinkedCard, (previousState, { flowId, card }) => {
        const flow = previousState.cardsGerenciamentoImoveis[flowId];
        const allCards = flow?.allCards ?? [];
        const exists = allCards.some((existing) => existing._id === card._id);
        const nextAllCards = exists
            ? allCards.map((existing) => (existing._id === card._id ? card : existing))
            : [...allCards, card];
        const deepLinkedCardIds = Array.from(
            new Set([...(flow?.deepLinkedCardIds ?? []), card._id]),
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...flow,
                    allCards: nextAllCards,
                    columns: flow?.columns ?? {},
                    deepLinkedCardIds,
                },
            }
        };
    }),
    on(setCards, (previousState, { cardsGerenciamentoImoveis, flowId }) => {
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    allCards: [  ...cardsGerenciamentoImoveis ],
                    attemptedSearchCards: true,
                },
            }
        }
    }),
    on(removeAnexos, (previousState, { cardId, flowId, anexoFileReferences }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        if (!flowAllCards) {
            console.error('[reducer/removeAnexos] flowId inválido — ação ignorada', { flowId, cardId });
            return previousState;
        }
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const newAnexos = card.anexos.filter(anexo => !anexoFileReferences.includes(anexo.fileReference));
                    return {
                        ...card,
                        anexos: newAnexos,
                    };
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateAnexos, (previousState, { cardId, flowId, anexoFileReference, newProperties }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        if (!flowAllCards) {
            console.error('[reducer/updateAnexos] flowId inválido — ação ignorada', { flowId, cardId });
            return previousState;
        }
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const updatedAnexos = card.anexos.map(anexo => {
                        if (anexo.fileReference === anexoFileReference) {
                            const newAnexo = {
                                ...anexo,
                                ...newProperties,
                            }
                            return newAnexo;
                        } else return anexo;
                    });
                    return {
                        ...card,
                        anexos: updatedAnexos,
                    };
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateCard, (previousState, { cardId, flowId, newCardData }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        if (!flowAllCards) {
            console.error('[reducer/updateCard] flowId inválido — ação ignorada', { flowId, cardId });
            return previousState;
        }

        const isArquivado = newCardData['arquivado'];
        const existing = flowAllCards.find(card => card._id === cardId);
        const previousStatus = (existing as any)?.status;
        const nextStatus = (newCardData as any)?.status;

        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    if (isArquivado) return null;
                    return {
                        ...card,
                        ...newCardData,
                    };
                } else return card;
            }
        ).filter(Boolean);

        let attemptedSearchArquivados = previousState.cardsGerenciamentoImoveis[flowId].attemptedSearchArquivados;
        if (previousState.cardsGerenciamentoImoveis[flowId].attemptedSearchArquivados) {
            attemptedSearchArquivados = false;
        }

        // Mantém os totais das colunas: arquivar tira da coluna de origem;
        // mudar de status move o total de uma coluna para a outra.
        let columns = previousState.cardsGerenciamentoImoveis[flowId].columns;
        if (isArquivado) {
            columns = adjustColumnTotal(columns, previousStatus, -1);
        } else if (nextStatus && previousStatus && nextStatus !== previousStatus) {
            columns = adjustColumnTotal(columns, previousStatus, -1);
            columns = adjustColumnTotal(columns, nextStatus, +1);
        }

        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                    columns,
                    attemptedSearchArquivados,
                }
            }
        };
    }),
    on(updateMultipleCards, (previousState, { cards, flowId }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedCardIds = cards.map(card => card._id);
        const updatedAllCards = flowAllCards.map(
            card => {
                if (updatedCardIds.includes(card._id)) {
                    const cardMatch = cards.find(cardMatch => cardMatch._id === card._id);
                    return {
                        ...card,
                        ...cardMatch,
                    };
                } else return card;
            }
        ).filter(Boolean);
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(removeCard, (previousState, { cardId, flowId }) => {
        const flow = previousState.cardsGerenciamentoImoveis[flowId];
        const flowAllCards = flow?.allCards;
        const removed = flowAllCards?.find(card => card._id === cardId);
        const updatedAllCards = flowAllCards.filter(card => card._id !== cardId);
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...flow,
                    allCards: updatedAllCards,
                    columns: adjustColumnTotal(flow?.columns, (removed as any)?.status, -1),
                }
            }
        };
    }),
    on(insertAnexos, (previousState, { cardId, flowId, anexos }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        if (!flowAllCards) {
            console.error('[reducer/insertAnexos] flowId inválido — ação ignorada', { flowId, cardId });
            return previousState;
        }
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        anexos: [ ...(card.anexos ?? []), ...anexos ],
                    };
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(insertComentario, (previousState, { cardId, flowId, comentario }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        comentarios: [ ...(card.comentarios ?? []), comentario ],
                    };
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(removeComentario, (previousState, { cardId, flowId, commentId  }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const commentIndex = card.comentarios.findIndex(comment => comment.commentId === commentId);
                    if (commentIndex >= 0)  {
                        return {
                            ...card,
                            comentarios: [ ...card.comentarios.slice(0, commentIndex), ...card.comentarios.slice(commentIndex + 1) ],
                        };
                    }
                    return card;
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(addEntry, (previousState, { cardId, fieldId, flowId, entry }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const calculadoraGerenciamentoFinanceiro = card.registroCalculadoraGerenciamentoFinanceiro?.calculadoraGerenciamentoFinanceiro;
                    if (!calculadoraGerenciamentoFinanceiro) return card;
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                            ...card.registroCalculadoraGerenciamentoFinanceiro,
                            calculadoraGerenciamentoFinanceiro: {
                                ...calculadoraGerenciamentoFinanceiro,
                                [fieldId]: {
                                    ...card.registroCalculadoraGerenciamentoFinanceiro.calculadoraGerenciamentoFinanceiro[fieldId],
                                    entries: [
                                        ...(card.registroCalculadoraGerenciamentoFinanceiro.calculadoraGerenciamentoFinanceiro[fieldId]?.entries ?? []),
                                        entry
                                    ]
                                }
                            }
                        }
                    }
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateEntry, (previousState, { cardId, fieldId, flowId, updatedEntry, previousFieldId }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        // Troca de categoria na edição: a entry sai da categoria anterior e entra na nova
        const movedFromFieldId = previousFieldId && previousFieldId !== fieldId ? previousFieldId : null;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const calculadoraGerenciamentoFinanceiro = card.registroCalculadoraGerenciamentoFinanceiro?.calculadoraGerenciamentoFinanceiro;
                    if (!calculadoraGerenciamentoFinanceiro) return card;
                    const targetEntries = calculadoraGerenciamentoFinanceiro?.[fieldId]?.entries ?? [];
                    const newEntries = movedFromFieldId
                        ? [...targetEntries.filter(entry => entry._id !== updatedEntry._id), updatedEntry]
                        : targetEntries.map(
                            entry => {
                              if (entry._id !== updatedEntry._id) return entry
                              else return updatedEntry
                            }
                        );
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                          ...card.registroCalculadoraGerenciamentoFinanceiro,
                          calculadoraGerenciamentoFinanceiro: {
                            ...calculadoraGerenciamentoFinanceiro,
                            [fieldId]: {
                              ...calculadoraGerenciamentoFinanceiro[fieldId],
                              entries: newEntries,
                            },
                            ...(movedFromFieldId ? {
                              [movedFromFieldId]: {
                                ...calculadoraGerenciamentoFinanceiro[movedFromFieldId],
                                entries: (calculadoraGerenciamentoFinanceiro[movedFromFieldId]?.entries ?? []).filter(
                                  entry => entry._id !== updatedEntry._id
                                ),
                              },
                            } : {}),
                          }
                        }
                      }
                } else return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(removeEntry, (previousState, { cardId, fieldId, flowId, entryId }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const calculadoraGerenciamentoFinanceiro = card.registroCalculadoraGerenciamentoFinanceiro?.calculadoraGerenciamentoFinanceiro;
                    if (!calculadoraGerenciamentoFinanceiro) return card;
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                            ...card.registroCalculadoraGerenciamentoFinanceiro,
                            calculadoraGerenciamentoFinanceiro: {
                                ...calculadoraGerenciamentoFinanceiro,
                                [fieldId]: {
                                    ...card.registroCalculadoraGerenciamentoFinanceiro.calculadoraGerenciamentoFinanceiro[fieldId],
                                    entries: (card.registroCalculadoraGerenciamentoFinanceiro
                                        .calculadoraGerenciamentoFinanceiro[fieldId]?.entries ?? []).filter(
                                            (entry: CalculadoraGerenciamentoFinanceiroEntry) => {
                                                return entry._id !== entryId
                                            }
                                        ),
                                }
                            }
                        }
                    }
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(setGruposTrabalho, (previousState, { gruposToSet }) => {
        return {
            ...previousState,
            gruposTrabalhoState: {
                allGruposTrabalho: gruposToSet,
                hasInitialLoad: true,
            }
        }
    }),
    on(updateGrupoTrabalho, (previousState, { grupoId, newProperties }) => {
        return {
            ...previousState,
            gruposTrabalhoState: {
                allGruposTrabalho: previousState.gruposTrabalhoState.allGruposTrabalho.map(
                    grupoTrabalho => {
                        return grupoTrabalho.id === grupoId
                            ?  ({
                                ...grupoTrabalho,
                                ...newProperties,
                            })
                            : grupoTrabalho;
                    }
                ),
                hasInitialLoad: previousState.gruposTrabalhoState.hasInitialLoad,
            }
        }
    }),
    on(removerGrupoTrabalho, (previousState, { grupoId }) => {
        return {
            ...previousState,
            gruposTrabalhoState: {
                allGruposTrabalho: previousState.gruposTrabalhoState.allGruposTrabalho.filter(
                    grupoTrabalho => grupoTrabalho.id !== grupoId
                ),
                hasInitialLoad: previousState.gruposTrabalhoState.hasInitialLoad,
            }
        }
    }),
    on(adicionarGrupoTrabalho, (previousState, { gruposToAdd }) => {
        return {
            ...previousState,
            gruposTrabalhoState: {
                allGruposTrabalho: [
                    ...gruposToAdd,
                    ...previousState.gruposTrabalhoState.allGruposTrabalho,
                ],
                hasInitialLoad: previousState.gruposTrabalhoState.hasInitialLoad,
            }
        }
    }),
    on(updateFieldConfig, (previousState, { cardId, fieldId, flowId, config }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    const calculadoraGerenciamentoFinanceiro = card.registroCalculadoraGerenciamentoFinanceiro?.calculadoraGerenciamentoFinanceiro;
                    if (!calculadoraGerenciamentoFinanceiro) return card;
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                            ...card.registroCalculadoraGerenciamentoFinanceiro,
                            calculadoraGerenciamentoFinanceiro: {
                                ...calculadoraGerenciamentoFinanceiro,
                                [fieldId]: {
                                    ...calculadoraGerenciamentoFinanceiro[fieldId],
                                    config,
                                }
                            }
                        }
                    };
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateAjusteConfig, (previousState, { cardId, flowId, config }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                            ...card.registroCalculadoraGerenciamentoFinanceiro,
                            ajusteConfig: config,
                        },
                    };
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateExigirComprovante, (previousState, { cardId, flowId, exigirComprovante }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        // Flow ainda não carregado (ex.: card aberto por deep link): nada a atualizar
        if (!flowAllCards) return previousState;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: {
                            ...card.registroCalculadoraGerenciamentoFinanceiro,
                            exigirComprovante,
                        },
                    };
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateExpenseResponsibilities, (previousState, { cardId, flowId, registro }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        registroCalculadoraGerenciamentoFinanceiro: registro,
                        registroCalculadoraGerenciamentoFinanceiroId: registro.id ?? card.registroCalculadoraGerenciamentoFinanceiroId,
                    };
                }
                return card;
            }
        );
        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                }
            }
        };
    }),
    on(updateCardContrato, (previousState, { cardId, flowId, newContratoData }) => {
        const flowAllCards = previousState.cardsGerenciamentoImoveis[flowId]?.allCards;
        const updatedAllCards = flowAllCards.map(
            card => {
                if (card._id === cardId) {
                    return {
                        ...card,
                        contrato: {
                            ...card.contrato,
                            ...newContratoData,
                        }
                    };
                } else return card;
            }
        ).filter(Boolean);
        let attemptedSearchArquivados = previousState.cardsGerenciamentoImoveis[flowId].attemptedSearchArquivados;
        if (previousState.cardsGerenciamentoImoveis[flowId].attemptedSearchArquivados) {
            attemptedSearchArquivados = false;
        }

        return {
            ...previousState,
            cardsGerenciamentoImoveis: {
                ...previousState.cardsGerenciamentoImoveis,
                [flowId]: {
                    ...previousState.cardsGerenciamentoImoveis[flowId],
                    allCards: updatedAllCards,
                    attemptedSearchArquivados,
                }
            }
        };
    })

);
